import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Segment } from "semantic-ui-react";
import {
  useDeleteLinkProduct,
  useGetAvailableProduct,
  useGetMainProduct,
  useGetRootProducts,
  useGetSubCompany,
  usePatchLinkProductVolume,
  usePostLinkProduct
} from "../../../../api/productMacting";
import { useGetAverageProductSummary } from "../../../../api/productSummary";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { warningMessage } from "../../../../helpers/ErrorHandler";
import MatchingCard from "./component/MatchingCard";
import MatchingTable from "./component/MatchingTable";

export default function ProductMatching({
  id,
  matchingContentView = true,
  matchingTableView = true,
  disableEdit = false
}: string | any) {
  const [selectedMainProduct, setSelectedMainProduct] = useState<any>("");
  const [selectedSubCompany, setSelectedSubCompany] = useState<string>("");
  const [tenantId, setTenantId] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [salesUnitId, setSalesUnitId] = useState<string | null>(null);
  const mainProductId = selectedMainProduct?._id || null;

  const {
    data: subCompany,
    isLoading: subCompanyLoading,
    isFetching: subCompanyFetching
  } = useGetSubCompany();

  const {
    data: mainProduct,
    isLoading: mainProductLoading,
    isFetching: mainProductFetching
  } = useGetMainProduct();

  const {
    data: availableProductDetails,
    isLoading: availableProductsLoading,
    isFetching: availableProductsFetching,
    refetch: getAvailableLinkProduct
  } = useGetAvailableProduct(tenantId);

  const availableProducts = useMemo(() => {
    return availableProductDetails?.filter((e: any) => e.key !== mainProductId) || [];
  }, [availableProductDetails]);

  const { data: productAverageSummaryData, refetch: refetchProductAverage } =
    useGetAverageProductSummary({
      productId: selectedMainProduct?._id,
      productIsAverage: selectedMainProduct?.is_average_product || false
    });

  const {
    data: rootProducts,
    refetch,
    isFetching: rootProductsFetching
  } = useGetRootProducts(mainProductId);

  const { mutate: patchLinkVolume, isLoading: loadLinkProductVolume } = usePatchLinkProductVolume();

  const { mutate: linkProduct, isLoading: loadLinkProduct } = usePostLinkProduct();
  const { mutate: deleteLinkProduct, isLoading: loadDeleteLinkProduct } = useDeleteLinkProduct();

  const initSubCompany = (subCompany: any) => {
    if (subCompany && subCompany.length > 0) {
      setTenantId(subCompany[0]?.tenantId ?? null);
      setSelectedSubCompany(subCompany[0] ?? null);
    } else {
      setTenantId(null);
      setSelectedSubCompany("");
    }
  };

  const initMainProduct = (mainProduct: any[], id: any) => {
    if (mainProduct && mainProduct.length > 0) {
      const findProduct = mainProduct.find((e: any) => e.key === id);
      setSelectedMainProduct(findProduct ?? null);
    } else {
      setSelectedMainProduct(null);
    }
  };

  useEffect(() => {
    initSubCompany(subCompany);
    initMainProduct(mainProduct, id);
  }, [subCompany, mainProduct, id]);

  const handleSubCompanyChange = (companyDetails: any) => {
    setSelectedSubCompany(companyDetails);
  };

  const addProductToMain = (subProduct: any) => {
    if (isEmpty(mainProductId)) return warningMessage("Please select main product");
    const linkProductDetails = {
      rootProductId: mainProductId,
      salesUnitId: subProduct?.salesUnitId
    };
    linkProduct(linkProductDetails, {
      onSuccess() {
        refetch();
        getAvailableLinkProduct();
      }
    });
  };

  const deleteLinkProductData = () => {
    deleteLinkProduct(
      {
        rootProductId: mainProductId,
        salesUnitId: salesUnitId
      },
      {
        onSuccess() {
          setDeleteStatus(false);
          refetch();
          getAvailableLinkProduct();
          refetchProductAverage();
        }
      }
    );
  };

  const setDeleteSalesUnit = (id: string) => {
    setSalesUnitId(id);
    setDeleteStatus(true);
  };

  const loadingStatus =
    loadLinkProduct ||
    subCompanyLoading ||
    subCompanyFetching ||
    mainProductLoading ||
    mainProductFetching ||
    availableProductsLoading ||
    availableProductsFetching ||
    rootProductsFetching ||
    loadDeleteLinkProduct;

  return (
    <Segment loading={loadingStatus}>
      {matchingContentView ? (
        <>
          <div className="tw-mb-8 tw-mt-8">
            <div className="tw-bg-main-background tw-border tw-border-gray-200 tw-rounded-lg tw-p-1 tw-shadow-sm">
              <h1 className="tw-text-2xl tw-font-bold tw-text-white">Match the Products</h1>
              <p className="tw-text-white">
                Connect main company products with sub-company alternatives
              </p>
            </div>
          </div>
          <MatchingCard
            addProductToMain={addProductToMain}
            handleSubCompanyChange={handleSubCompanyChange}
            selectedSubCompany={selectedSubCompany}
            availableProducts={availableProducts}
            subCompany={subCompany}
            setTenantId={setTenantId}
            productAverageSummaryData={productAverageSummaryData}
            rootProducts={rootProducts}
          />
        </>
      ) : null}
      {matchingTableView ? (
        <MatchingTable
          currentMainProduct={rootProducts}
          removeLinkedProduct={setDeleteSalesUnit}
          disableEdit={disableEdit}
          patchLinkVolume={patchLinkVolume}
          refetchProductAverage={refetchProductAverage}
        />
      ) : null}
      <ConfirmModal
        viewModal={deleteStatus}
        closeModal={() => setDeleteStatus(false)}
        cancel={() => {
          setDeleteStatus(false);
        }}
        approve={() => {
          deleteLinkProductData();
        }}
        title="Delete Link Product"
        subTitle="Are you sure you want to delete link product?"
      />
    </Segment>
  );
}
